import { currentUser } from "lib/currentUser"
import { wrapInUserModel } from "models/user"

let copyUrlTimeout

const showMessage = (copyUrlEl, shareArticleMessageEl) => {
  copyUrlEl.classList.remove("opacity-100")
  copyUrlEl.classList.add("opacity-0")
  shareArticleMessageEl.classList.remove("opacity-0")
  shareArticleMessageEl.classList.add("!text-white", "opacity-100", "!w-full", "z-20")
}

const hideMessage = (copyUrlEl, shareArticleMessageEl) => {
  copyUrlEl.classList.remove("opacity-0")
  copyUrlEl.classList.add("opacity-100")
  shareArticleMessageEl.classList.remove("!text-white", "opacity-100", "!w-full", "z-20")
  shareArticleMessageEl.classList.add("opacity-0")
  copyUrlTimeout = undefined
}

const handleClickLink = ({ element, fallbackHandler }) => {
  // iOS has some really weird behaviour with clipped text, so on iOS
  // we want to avoid the user interacting with the url in the clipped element
  // normally, just treat it like we click the copy url button.
  //
  // Other user agents and browsers can interact with the clipped text normally.
  if (navigator.platform.match(/iPhone|iPad/)) {
    fallbackHandler()
  } else if (element) {
    window.getSelection().selectAllChildren(element)
  }
}

const handleCopyUrl = async ({ linkEl, copyUrlEl, shareArticleMessageEl }) => {
  const url = new URL(linkEl.textContent.trim())
  const params = new URLSearchParams(url.search)
  params.set("utm_medium", "article_clipboard_share")
  params.set("utm_source", "theconversation.com")
  url.search = params.toString()

  try {
    await navigator.clipboard.writeText(url.href)

    if (copyUrlTimeout) {
      clearTimeout(copyUrlTimeout)
      hideMessage(copyUrlEl, shareArticleMessageEl)
      setTimeout(showMessage.bind(undefined, copyUrlEl, shareArticleMessageEl), 150)
    } else {
      setTimeout(showMessage.bind(undefined, copyUrlEl, shareArticleMessageEl))
    }

    copyUrlTimeout = setTimeout(hideMessage.bind(undefined, copyUrlEl, shareArticleMessageEl), 2500)
  } catch (e) {
    // if for some reason the clipboard is blocked, give them a native prompt to copy from
    prompt(undefined, url.href)
  }
}

const handleNativeShare = async ({ linkEl, fallbackHandler }) => {
  if (navigator.share) {
    const url = new URL(linkEl.textContent.trim())
    const params = new URLSearchParams(url.search)
    params.set("utm_medium", "article_native_share")
    params.set("utm_source", "theconversation.com")
    url.search = params.toString()

    try {
      await navigator.share({
        url: url.href,
      })
    } catch (e) {
      // intentionally do nothing, some browsers seem to raise
      // an error here if the user cancels the share intent, we
      // protect against the share API not being available at
      // the start of this function
    }
  } else {
    // if the browser doesn't support native share for whatever reason
    // just fall back to copying to clipboard
    fallbackHandler()
  }
}

export default async function initShareButtons() {
  const copyUrlEl = document.getElementById("share-article-copy-url")
  const nativeShareEl = document.getElementById("share-article-native")
  const shareArticleMessageEl = document.getElementById("share-article-message")
  const linkEl = document.getElementById("share-article-url")

  if (copyUrlEl && nativeShareEl && shareArticleMessageEl && linkEl) {
    const boundHandleCopyUrl = handleCopyUrl.bind(undefined, {
      linkEl,
      copyUrlEl,
      shareArticleMessageEl,
    })
    const boundHandleNativeShare = handleNativeShare.bind(undefined, {
      linkEl,
      fallbackHandler: boundHandleCopyUrl,
    })
    const boundHandleClickLink = handleClickLink.bind(undefined, { linkEl, boundHandleCopyUrl })

    // prevent flicker of unstyled content, this is an unfortunate side effect
    // of tailwind styles loading "late" which we should ideally fix
    copyUrlEl.classList.add("transition-all", "duration-300")

    copyUrlEl.addEventListener("click", boundHandleCopyUrl)
    nativeShareEl.addEventListener("click", boundHandleNativeShare)
    linkEl.addEventListener("click", boundHandleClickLink)
  }

  const user = await currentUser().then(wrapInUserModel)

  if (user.hasAccessTo("native_share")) {
    document.body.classList.add("native-share")
  }
}
